<template>
  <div>
    <empty-state
      v-if="isLoading || !data || data.length === 0"
      :loading="isLoading"
      :title="isLoading ? $t('loading') : $t('no_data_available')"
      :subtitle="!isLoading ? $t('no_restaurants_found') : ''"
      icon="mdi-silverware"
    />

    <div v-else class="pa-1">
      <!-- Mobile View -->
      <div v-if="device.isMobile">
        <v-card
          v-for="(item, index) in customSearch(data, mobileHeaders, searchQuery)"
          :key="index"
          class="mb-3 ordera-box-shadow"
        >
          <v-card-title class="py-2">
            <v-icon left color="primary">mdi-silverware</v-icon>
            <span class="font-weight-bold">{{ item.desk_name }}</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="py-2">
            <v-row dense>
              <v-col cols="12">
                <div class="text-body-2">
                  <v-icon small class="mr-2 grey--text">mdi-account</v-icon>
                  {{ item.name }}
                </div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">
                  {{ $t("ordered") }}
                </div>
                <div class="font-weight-medium">
                  {{ item.orders }}
                </div>
              </v-col>
              <v-col cols="6" class="text-right">
                <div class="text-caption grey--text">
                  {{ $t("price1") }}
                </div>
                <div class="font-weight-bold primary--text">
                  {{ item.price }} {{ restaurantCurrency }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </div>

      <!-- Desktop View -->
      <v-data-table
        v-else
        :headers="headers"
        :items="customSearch(data, headers, searchQuery)"
        class="striped-table ordera-box-shadow"
        :loading="isLoading"
        hide-default-footer
        disable-pagination
      >
        <template v-slot:item.num="{ index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.price="{ item }">
          <span class="font-weight-bold primary--text">
            {{ item.price }} {{ restaurantCurrency }}
          </span>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import axios from "@/plugins/axios";

export default {
  name: "AdminRestaurantsTab",
  mixins: [hasPrinting, hasExcel],
  components: {
    EmptyState,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    isActiveTab: Boolean,
    selectedRestaurants: Array,
    restaurantIds: String,
  },
  data() {
    return {
      data: [],
      isLoading: false,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("table"), value: "desk_name", sortable: false },
        { text: this.$t("name_lastname"), value: "name", sortable: false },
        { text: this.$t("ordered"), value: "orders", sortable: false, align: "center" },
        { text: this.$t("price1"), value: "price", sortable: false },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("table"), value: "desk_name", sortable: false },
        { text: this.$t("name_lastname"), value: "name", sortable: false },
        { text: this.$t("ordered"), value: "orders", sortable: false },
        { text: this.$t("price1"), value: "price", sortable: false },
      ];
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (!isPrinting || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.printRestaurantsTable();
    },
    isExcelDownloading(isDownloading) {
      if (!isDownloading || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.exportRestaurantsExcel();
    },
    dateRange: {
      handler() {
        this.loadData();
      },
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
        };

        // TODO: Replace with actual API endpoint when available
        const response = await axios.get("/admin/restaurants-report", { params });
        this.data = response.data.data || response.data;
      } catch (error) {
        console.error("Error loading restaurants data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    printRestaurantsTable() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        desk_name: item.desk_name || "",
        name: item.name || "",
        orders: item.orders || 0,
        price: `${item.price || 0} ${this.restaurantCurrency}`,
      }));

      this.printDocument({
        pageTitle: this.$t("restaurants"),
        title: `${this.$t("restaurants")} - ${this.$t("report")}`,
        subtitle: `${this.$t("date")}: ${this.dateRange[0]} - ${this.dateRange[1]}`,
        tables: [
          {
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
    exportRestaurantsExcel() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        desk_name: item.desk_name || "",
        name: item.name || "",
        orders: item.orders || 0,
        price: `${item.price || 0} ${this.restaurantCurrency}`,
      }));

      this.downloadExcel({
        fileName: `admin_poslovnice_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t("restaurants"),
            title: `${this.$t("restaurants")} - ${this.$t("report")}`,
            subtitle: `${this.$t("date")}: ${this.dateRange[0]} - ${this.dateRange[1]}`,
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
  },
};
</script>
